/**
 * 界面图标。
 *
 * 全部用描边 SVG，不用 emoji ——
 * emoji 在 Windows / macOS / Linux 上是三套完全不同的画风（彩色、扁平、线条），
 * 同一个界面在不同机器上看着像两个产品；而且它们自带颜色，
 * 没法跟着「选中 / 禁用 / 危险」这些状态变色，只能硬摆在那里。
 *
 * 描边图标跟随 currentColor，粗细统一 1.8，24×24 画布，
 * 和腾讯会议、飞书这类会议软件的图标语言一致。
 */

type P = { size?: number; className?: string }

/** 所有图标共用的外壳：统一画布、描边粗细与端点形状。 */
function S({ size = 22, className, children }: P & { children: React.ReactNode }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {children}
    </svg>
  )
}

/** 麦克风（开）。 */
export const IcMic = (p: P) => (
  <S {...p}>
    <rect x="9" y="2.5" width="6" height="11" rx="3" />
    <path d="M5.5 11a6.5 6.5 0 0 0 13 0" />
    <path d="M12 17.5V21" />
    <path d="M8.5 21h7" />
  </S>
)

/** 麦克风（关）：带一道斜杠，和「开」是同一个轮廓，只加否定笔画。 */
export const IcMicOff = (p: P) => (
  <S {...p}>
    <path d="M9 5.2A3 3 0 0 1 15 5.5v5" />
    <path d="M15 13.2a3 3 0 0 1-6-2.2V9" />
    <path d="M5.5 11a6.5 6.5 0 0 0 9.8 5.6M18.5 11v.4" />
    <path d="M12 17.5V21" />
    <path d="M8.5 21h7" />
    <path d="M3.5 3.5l17 17" />
  </S>
)

/** 摄像头（开）。 */
export const IcCam = (p: P) => (
  <S {...p}>
    <rect x="2.5" y="6" width="13" height="12" rx="2.5" />
    <path d="M15.5 10.5l5-2.6v8.2l-5-2.6z" />
  </S>
)

/** 摄像头（关）。 */
export const IcCamOff = (p: P) => (
  <S {...p}>
    <path d="M15.5 13.5V16a2 2 0 0 1-2 2H4.5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h1" />
    <path d="M9.5 6h4a2 2 0 0 1 2 2v2.5l5-2.6v8.2l-2.4-1.2" />
    <path d="M3.5 3.5l17 17" />
  </S>
)

/** 扬声器（开）。 */
export const IcSpeaker = (p: P) => (
  <S {...p}>
    <path d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4z" />
    <path d="M15.5 9.2a4 4 0 0 1 0 5.6" />
    <path d="M18 6.8a7.5 7.5 0 0 1 0 10.4" />
  </S>
)

/** 扬声器（静音）。 */
export const IcSpeakerOff = (p: P) => (
  <S {...p}>
    <path d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4z" />
    <path d="M16 10l4.5 4.5M20.5 10L16 14.5" />
  </S>
)

/** 举手。 */
export const IcHand = (p: P) => (
  <S {...p}>
    <path d="M8.5 11V4.8a1.4 1.4 0 0 1 2.8 0V10" />
    <path d="M11.3 10V3.6a1.4 1.4 0 0 1 2.8 0V10" />
    <path d="M14.1 10.2V5.6a1.4 1.4 0 0 1 2.8 0V13" />
    <path d="M8.5 11l-1.9 1.2a1.6 1.6 0 0 0-.5 2.2l2.6 4.3A4.5 4.5 0 0 0 12.5 21h1.6a4.5 4.5 0 0 0 4.5-4.5V13" />
  </S>
)

/** 表情。 */
export const IcSmile = (p: P) => (
  <S {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 14.2a4.4 4.4 0 0 0 7 0" />
    <path d="M9 9.5v.01M15 9.5v.01" strokeWidth={2.4} />
  </S>
)

/** 共享屏幕。 */
export const IcShare = (p: P) => (
  <S {...p}>
    <rect x="2.5" y="4" width="19" height="13" rx="2" />
    <path d="M8.5 21h7" />
    <path d="M12 8.5v5M9.5 11l2.5-2.5L14.5 11" />
  </S>
)

/** 录制（未开始）：一个实心圆点。 */
export const IcRecord = (p: P) => (
  <S {...p}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />
  </S>
)

/** 录制（进行中）：方块表示「停止」。 */
export const IcRecordStop = (p: P) => (
  <S {...p}>
    <circle cx="12" cy="12" r="9" />
    <rect x="8.8" y="8.8" width="6.4" height="6.4" rx="1.2" fill="currentColor" stroke="none" />
  </S>
)

/** 暂停。 */
export const IcPause = (p: P) => (
  <S {...p}>
    <path d="M9.5 5v14M14.5 5v14" strokeWidth={2.2} />
  </S>
)

/** 继续。 */
export const IcPlay = (p: P) => (
  <S {...p}>
    <path d="M7.5 5.2l11 6.8-11 6.8z" />
  </S>
)

/** 停止（共享）。 */
export const IcStop = (p: P) => (
  <S {...p}>
    <rect x="5.5" y="5.5" width="13" height="13" rx="2" />
  </S>
)

/** 更多：三个点。 */
export const IcMore = (p: P) => (
  <S {...p}>
    <circle cx="5.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="18.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
  </S>
)

/** 退出教室：会议软件通用的「挂断」话筒。 */
export const IcHangup = (p: P) => (
  <S {...p}>
    <path d="M2.8 13.2c5-4.6 13.4-4.6 18.4 0l-2.3 2.5a1.7 1.7 0 0 1-2.2.2l-1.9-1.4a1.4 1.4 0 0 1-.5-1.5l.3-1.1a11 11 0 0 0-5.2 0l.3 1.1a1.4 1.4 0 0 1-.5 1.5l-1.9 1.4a1.7 1.7 0 0 1-2.2-.2z" />
  </S>
)

/* ── 白板工具 ─────────────────────────────────────── */

/** 画笔。 */
export const IcPen = (p: P) => (
  <S {...p}>
    <path d="M16.4 3.6l4 4L8.8 19.2l-5.3 1.3 1.3-5.3z" />
    <path d="M14.4 5.6l4 4" />
  </S>
)

/** 直线。 */
export const IcLine = (p: P) => (
  <S {...p}>
    <path d="M4.5 19.5L19.5 4.5" />
    <circle cx="4.5" cy="19.5" r="1.8" />
    <circle cx="19.5" cy="4.5" r="1.8" />
  </S>
)

/** 矩形。 */
export const IcRect = (p: P) => (
  <S {...p}>
    <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" />
  </S>
)

/** 椭圆。 */
export const IcEllipse = (p: P) => (
  <S {...p}>
    <ellipse cx="12" cy="12" rx="8.5" ry="6.5" />
  </S>
)

/** 文字。 */
export const IcText = (p: P) => (
  <S {...p}>
    <path d="M4.5 6.5V4.5h15v2" />
    <path d="M12 4.5v15" />
    <path d="M8.5 19.5h7" />
  </S>
)

/** 橡皮。 */
export const IcEraser = (p: P) => (
  <S {...p}>
    <path d="M8.6 20.5H20" />
    <path d="M14.2 3.9l5.9 5.9a1.7 1.7 0 0 1 0 2.4l-7.3 7.3a1.7 1.7 0 0 1-2.4 0l-5.9-5.9a1.7 1.7 0 0 1 0-2.4l7.3-7.3a1.7 1.7 0 0 1 2.4 0z" />
    <path d="M7.6 10.5l5.9 5.9" />
  </S>
)

/** 选择。 */
export const IcSelect = (p: P) => (
  <S {...p}>
    <path d="M5.5 3.5l13.6 6.6-5.7 1.8-1.8 5.7z" />
    <path d="M13.6 13.6l5.4 5.4" />
  </S>
)

/** 公告喇叭。 */
export const IcMegaphone = (p: P) => (
  <S {...p}>
    <path d="M3.5 10v4a1.5 1.5 0 0 0 1.5 1.5h2L14 20V4L7 8.5H5A1.5 1.5 0 0 0 3.5 10z" />
    <path d="M17.5 9.4a3.6 3.6 0 0 1 0 5.2" />
    <path d="M7 15.5V20a1.5 1.5 0 0 0 3 0v-3.2" />
  </S>
)

/** 聊天气泡（手机底栏的交流入口）。 */
export const IcChat = (p: P) => (
  <S {...p}>
    <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8a2.5 2.5 0 0 1-2.5 2.5H10l-4.6 3.6c-.6.5-1.4 0-1.4-.7z" />
    <path d="M8 8.5h8M8 12h5" />
  </S>
)

/** 成员：两个人的剪影（腾讯会议工具条的「成员」） */
export const IcUsers = (p: P) => (
  <S {...p}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
    <circle cx="16.5" cy="9" r="2.5" />
    <path d="M15.5 14.2c2.7.2 5 2 5 4.8" />
  </S>
)
